/**
 * @module app.core
 */

import { autoinject } from 'aurelia-framework';
import { AssessmentContext } from 'app/core/lib/assessment-context';
import { ApiDateTime } from 'app/shared/models/essette-api';
import { IQuestionComponent, IQuestionConfig } from 'app/core/components/assessments/interfaces';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { QuestionBase } from 'app/core/components/assessments/questions/question-base';
import * as Core from 'app/core/models/essette-api';
import { EnvironmentService } from 'app/shared/services/environment-service';


@autoinject
export class QuestionDatetimeSecondsCustomElement extends QuestionBase implements IQuestionComponent {

    ////****************** Fields and Properties **************************************////
    protected selectedDateTime: ApiDateTime | null = null;
    protected history: Core.AssessmentAnswer[] = [];


    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(protected readonly env: EnvironmentService, protected readonly context: AssessmentContext, private readonly logger: AppLogger) {
        super(context);
        this.logger.source = this;

        if(!context || !context.currentSession) {
            throw new Error("No Assessment context is available");
        }

        if(!this.context.questionComponents.includes(this)) {
            this.context.questionComponents.push(this);
        }
    }

    protected async activate(config: IQuestionConfig): Promise<void> {
        this.question = config.question;
        this.required = !!this.question.flag?.isRequired;
        this.showHistory = config.showHistory;
        this.readonly = config.readonly;
        this.isValid = !this.required;
        this.labelId = config.labelId || null;
    }

    protected attached(): void {
        this.loadPreviousAnswer();

        if(this.showHistory && this.context && this.context.attachment && this.context.attachment.history && this.context.attachment.history.answers) {
            this.history = this.context.attachment.history.answers
                .filter(a => { return a.question!.id === this.question!.id; })
                .map(a => {
                    return {
                        ...a,
                        value: this.parseDateTimeValue(a.value || '')
                    };
                });
        }
    }


    ////****************** Functions ****************************************************////
    private async loadPreviousAnswer(): Promise<void> {
        const questionId = this.question && this.question.id;
        if(!questionId || !this.context.attachment.currentAnswers) { return; }

        const description = this.question && this.question.description || '';

        try {
            const answer = this.context.attachment.currentAnswers.find(a => {
                if(!a.question) { return false; }

                if(!this.question.sharedQuestion) {
                    return a.question.id === this.question.id;
                } else if(!!this.question.sharedQuestion && !!a.sharedQuestion) {
                    return this.question.sharedQuestion.id === a.sharedQuestion.id;
                }

                return false;

            });

            if(answer && answer.value) {
                // Parse datetime format: "20260609 19:41:50" -> "2026-06-09T19:41:50.000Z"
                // Transitional stores datetimes in a non-standard format, so we need to parse it before using. Answers from 4x should already be in ISO format.
                const parsed = this.parseDateTimeValue(answer.value);
                this.selectedDateTime = parsed || null;
            }
        }
        catch(error) {
            this.logger.error(`Find existing answer error: ${error}`);
            this.logger.error(`Unable to find existing answer(s) for question ${questionId} (${description})`);
            throw error;
        }
    }

    private parseDateTimeValue(value: string): string | null {
        if(!value || typeof value !== 'string') { return null; }

        // Check if data matches this pattern, then convert: "20260609 19:41:50" -> "2026-06-09T19:41:50.000Z"
        const match = value.match(/^(\d{4})(\d{2})(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
        if(match) {
            const [, year, month, day, hours, minutes, seconds] = match;
            return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000Z`;
        }

        // If already in ISO format or other standard format, return as-is
        return value;
    }
    protected setDateTimeNow(): void {
        this.selectedDateTime = this.env.now()
    }


    ////****************** IQuestionComponent ****************************************************////
    public saveAnswer = null;  // Not Implemented

    public getAnswers(): Core.AssessmentAnswer[] {
        if(this.selectedDateTime && this.selectedDateTime.length > 0) {
            const answer: Core.AssessmentAnswer = {
                question: { id: this.question && this.question.id || null },
                text: this.selectedDateTime
            };

            return [answer];
        }

        return [];
    }

}
